import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import type { JSONContent } from '@tiptap/core';
import Button from 'devextreme-react/button';
import LoadIndicator from 'devextreme-react/load-indicator';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { custom } from 'devextreme/ui/dialog';
import { displayNameFromPath } from '../mediaViewer/mediaFileTypes';
import {
  fileAccessRestrictedHref,
  isFileAccessRestrictedStatus
} from '../storageAccess/fileAccessRestrictedUrls';
import { useShellToolbarCenter } from '../shell/ShellToolbarContext';
import { showErrorToast, showSuccessToast, showWarningToast } from '../notifications/toast';
import {
  ApiHttpError,
  ContentRevisionConflictError,
  copyPathForConflict,
  fetchContent,
  saveContent,
  type StorageContentResponse
} from './markdownEditorApi';
import {
  detectDocFormat,
  type DocFormat,
  contentTypeForDocFormat
} from './docTypes';
import {
  DocCodecError,
  parseMarkdownToJson,
  parseStorageContent,
  resolveSaveContentType,
  serializeDocForStorage,
  serializeJsonToMarkdown
} from './docCodecs';
import { createMarkdownEditorExtensions } from './markdownEditorExtensions';
import { uploadJdocAsset } from './jdocAssetUpload';
import { MarkdownPreviewPane } from './MarkdownPreviewPane';
import { MarkdownSourcePane } from './MarkdownSourcePane';
import {
  readMarkdownEditorLayoutPreference,
  writeMarkdownEditorLayoutPreference,
  type MarkdownEditorLayoutMode
} from './markdownEditorLayoutPrefs';
import { getEditorAutosaveIntervalMs } from '../editorShell/editorAutosavePreference';
import {
  isEmbeddedEditor,
  resolveEditorChromeMode,
  type EditorChromeMode
} from '../editorShell/editorChrome';
import { MinimalEditorChrome } from '../editorShell/MinimalEditorChrome';
import './markdownEditor.css';

const WIDTH_PREF_KEY = 'jarvis-markdown-editor-width';
const PREVIEW_DEBOUNCE_MS = 200;

type LoadState = 'idle' | 'loading' | 'loaded' | 'error';
type WidthMode = 'full' | 'constrained';
type LayoutMode = MarkdownEditorLayoutMode;

type MarkdownStorage = {
  markdown: {
    getMarkdown: () => string;
  };
};

function readLiveMarkdown(editor: { storage: unknown } | null): string {
  if (!editor) return '';
  try {
    return (editor.storage as MarkdownStorage).markdown.getMarkdown();
  } catch {
    return '';
  }
}

export interface MarkdownEditorPageProps {
  filesLinkTo?: string;
  filesLinkLabel?: string;
  chrome?: EditorChromeMode;
}

function readWidthPreference(): WidthMode {
  if (typeof localStorage === 'undefined') return 'full';
  const stored = localStorage.getItem(WIDTH_PREF_KEY);
  if (stored === 'constrained') return 'constrained';
  return 'full';
}

function writeWidthPreference(mode: WidthMode): void {
  localStorage.setItem(WIDTH_PREF_KEY, mode);
}

async function showRevisionConflictDialog(): Promise<'reload' | 'copy' | 'cancel'> {
  const dialog = custom({
    title: 'Revision conflict',
    messageHtml:
      '<p style="margin:0;line-height:1.45">The file changed on the server since you opened it. Reload the latest version or save your edits as a copy.</p>',
    buttons: [
      {
        text: 'Reload',
        type: 'default',
        stylingMode: 'contained',
        onClick: () => 'reload' as const
      },
      {
        text: 'Save as copy',
        stylingMode: 'outlined',
        onClick: () => 'copy' as const
      },
      {
        text: 'Cancel',
        stylingMode: 'text',
        onClick: () => 'cancel' as const
      }
    ]
  });

  try {
    const result = await dialog.show();
    if (result === 'reload' || result === 'copy' || result === 'cancel') return result;
    return 'cancel';
  } catch {
    return 'cancel';
  }
}

function formatLabel(format: DocFormat): string {
  return format === 'markdown' ? 'Markdown' : 'JDoc';
}

export function MarkdownEditorPage({
  filesLinkTo = '/files',
  filesLinkLabel = 'File Browser',
  chrome: chromeProp
}: MarkdownEditorPageProps) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const mountId = (params.get('mountId') ?? '').trim();
  const path = (params.get('path') ?? '').trim();
  const viewOnly = (params.get('mode') ?? '').trim().toLowerCase() === 'view';
  const hasSource = Boolean(mountId && path);
  const pathFormat = useMemo(() => (path ? detectDocFormat(path) : null), [path]);
  const embedded = isEmbeddedEditor(params);
  const chrome = resolveEditorChromeMode(params, chromeProp);

  useShellToolbarCenter(null);

  const [loadState, setLoadState] = useState<LoadState>(hasSource ? 'loading' : 'idle');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [docFormat, setDocFormat] = useState<DocFormat | null>(pathFormat);
  const [contentType, setContentType] = useState('');
  const [revision, setRevision] = useState<string | null>(null);
  const [savedSerialized, setSavedSerialized] = useState('');
  const [contentVersion, setContentVersion] = useState(0);
  const [widthMode, setWidthMode] = useState<WidthMode>(readWidthPreference);
  const [layoutMode, setLayoutMode] = useState<LayoutMode>(() =>
    viewOnly ? 'preview' : readMarkdownEditorLayoutPreference()
  );
  const [sourceMarkdown, setSourceMarkdown] = useState('');
  const [savedSourceMarkdown, setSavedSourceMarkdown] = useState('');
  const [sourceHydrated, setSourceHydrated] = useState(false);
  const [editorHydrated, setEditorHydrated] = useState(false);
  const [previewMarkdown, setPreviewMarkdown] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  // Workspace embed still shows the formatting toolbar; keep reading width preference
  // but default/full is preferred for file-browser editing.
  const effectiveWidthMode: WidthMode = widthMode;
  const splitActive = layoutMode === 'split';
  const previewOnlyActive = layoutMode === 'preview';
  const sourceBackedActive = layoutMode !== 'edit';

  const revisionRef = useRef<string | null>(null);
  const savingRef = useRef(false);
  const pathRef = useRef(path);
  const mountIdRef = useRef(mountId);
  const docFormatRef = useRef<DocFormat | null>(pathFormat);
  const contentTypeRef = useRef('');
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const pendingDocRef = useRef<JSONContent | null>(null);
  const sourceMarkdownRef = useRef('');
  const applyingSourceToEditorRef = useRef(false);
  const sourceHydratedRef = useRef(false);
  const editorHydratedRef = useRef(false);
  const sourceBackedActiveRef = useRef(sourceBackedActive);

  sourceMarkdownRef.current = sourceMarkdown;
  sourceHydratedRef.current = sourceHydrated;
  editorHydratedRef.current = editorHydrated;
  sourceBackedActiveRef.current = sourceBackedActive;

  pathRef.current = path;
  mountIdRef.current = mountId;
  revisionRef.current = revision;
  docFormatRef.current = docFormat;
  contentTypeRef.current = contentType;

  const title = path ? displayNameFromPath(path) : '';

  const editor = useEditor({
    extensions: createMarkdownEditorExtensions({ placeholder: 'Start writing…' }),
    editable: loadState === 'loaded' && !viewOnly,
    onUpdate: () => setContentVersion((value) => value + 1)
  });

  const currentSerialized = useMemo(() => {
    void contentVersion;
    if (!editor || !docFormat) return '';
    return serializeDocForStorage(docFormat, editor.getJSON());
  }, [contentVersion, docFormat, editor]);

  const dirty =
    !viewOnly &&
    loadState === 'loaded' &&
    (sourceBackedActive
      ? sourceHydrated && sourceMarkdown !== savedSourceMarkdown
      : editorHydrated && currentSerialized !== savedSerialized);

  const applyLoadedContent = useCallback(
    (payload: StorageContentResponse) => {
      try {
        const parsed = parseStorageContent(payload.path, payload.content);
        const serialized = serializeDocForStorage(parsed.format, parsed.doc);
        const source =
          parsed.format === 'markdown' ? payload.content : serializeJsonToMarkdown(parsed.doc);
        setDocFormat(parsed.format);
        docFormatRef.current = parsed.format;
        setContentType(payload.contentType || contentTypeForDocFormat(parsed.format));
        setRevision(payload.revision);
        setSavedSerialized(serialized);
        setSourceMarkdown(source);
        sourceMarkdownRef.current = source;
        setSavedSourceMarkdown(source);
        setPreviewMarkdown(source);
        setSourceHydrated(true);
        sourceHydratedRef.current = true;
        setEditorHydrated(false);
        editorHydratedRef.current = false;
        pendingDocRef.current = parsed.doc;
        setLoadState('loaded');
        setLoadError(null);
      } catch (error) {
        const message = error instanceof DocCodecError ? error.message : 'Failed to parse document.';
        setLoadState('error');
        setLoadError(message);
      }
    },
    []
  );

  useEffect(() => {
    if (!editor || !pendingDocRef.current) return;
    editor.commands.setContent(pendingDocRef.current);
    pendingDocRef.current = null;
    setEditorHydrated(true);
    editorHydratedRef.current = true;
    setContentVersion((value) => value + 1);
  }, [editor, loadState]);

  const reloadContent = useCallback(async () => {
    if (!mountIdRef.current || !pathRef.current) return;
    setSourceHydrated(false);
    sourceHydratedRef.current = false;
    setEditorHydrated(false);
    editorHydratedRef.current = false;
    setLoadState('loading');
    setLoadError(null);
    try {
      const payload = await fetchContent(mountIdRef.current, pathRef.current);
      applyLoadedContent(payload);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load file.';
      setLoadState('error');
      setLoadError(message);
      showErrorToast(message);
    }
  }, [applyLoadedContent]);

  useEffect(() => {
    if (!hasSource) {
      setLoadState('idle');
      setLoadError(null);
      setDocFormat(null);
      return;
    }

    if (!pathFormat) {
      setLoadState('error');
      setLoadError('Only .md, .markdown, and .jdoc files can be opened here.');
      return;
    }

    let cancelled = false;
    setSourceHydrated(false);
    sourceHydratedRef.current = false;
    setEditorHydrated(false);
    editorHydratedRef.current = false;
    setLoadState('loading');
    setLoadError(null);
    setDocFormat(pathFormat);

    void (async () => {
      try {
        const payload = await fetchContent(mountId, path);
        if (cancelled) return;
        applyLoadedContent(payload);
      } catch (error) {
        if (cancelled) return;
        if (error instanceof ApiHttpError && isFileAccessRestrictedStatus(error.status)) {
          navigate(fileAccessRestrictedHref(), { replace: true });
          return;
        }
        const message = error instanceof Error ? error.message : 'Failed to load file.';
        setLoadState('error');
        setLoadError(message);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [applyLoadedContent, hasSource, mountId, navigate, path, pathFormat]);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = title ? `${title} — Markdown Editor` : 'JARVIS Markdown Editor';
    return () => {
      document.title = previousTitle;
    };
  }, [title]);

  useEffect(() => {
    editor?.setEditable(loadState === 'loaded');
  }, [editor, loadState]);

  const persist = useCallback(
    async (options?: { targetPath?: string; skipRevisionCheck?: boolean; silent?: boolean }) => {
      const activeMountId = mountIdRef.current;
      const activePath = options?.targetPath ?? pathRef.current;
      const format = docFormatRef.current;
      if (!activeMountId || !activePath || !format || !editor || savingRef.current) return false;
      if (sourceBackedActiveRef.current && !sourceHydratedRef.current) {
        if (!options?.silent) showWarningToast('The Markdown source is still loading.');
        return false;
      }
      if (!sourceBackedActiveRef.current && !editorHydratedRef.current) {
        if (!options?.silent) showWarningToast('The Markdown editor is still loading.');
        return false;
      }

      savingRef.current = true;
      setSaving(true);
      try {
        let content: string;
        let canonicalSerialized: string;
        let sourceForBaseline: string;
        let sourceDoc: JSONContent | null = null;
        if (sourceBackedActiveRef.current) {
          sourceForBaseline = sourceMarkdownRef.current;
          sourceDoc = parseMarkdownToJson(sourceForBaseline);
          canonicalSerialized = serializeDocForStorage(format, sourceDoc);
          content = format === 'markdown' ? sourceForBaseline : canonicalSerialized;
        } else {
          sourceDoc = editor.getJSON();
          canonicalSerialized = serializeDocForStorage(format, sourceDoc);
          content = canonicalSerialized;
          sourceForBaseline = readLiveMarkdown(editor);
        }
        const expectedRevision =
          options?.skipRevisionCheck || options?.targetPath
            ? undefined
            : revisionRef.current ?? undefined;

        const result = await saveContent(activeMountId, {
          path: activePath,
          content,
          contentType: resolveSaveContentType(activePath, format, contentTypeRef.current),
          expectedRevision
        });
        setRevision(result.revision);
        setSavedSerialized(canonicalSerialized);
        setSavedSourceMarkdown(sourceForBaseline);
        if (sourceDoc && sourceBackedActiveRef.current) {
          applyingSourceToEditorRef.current = true;
          editor.commands.setContent(sourceDoc);
          window.setTimeout(() => {
            applyingSourceToEditorRef.current = false;
          }, 0);
        }
        if (options?.targetPath && options.targetPath !== pathRef.current) {
          setParams({ mountId: activeMountId, path: result.path }, { replace: true });
        }
        if (!options?.silent) {
          showSuccessToast(options?.targetPath ? 'Saved as copy.' : 'Saved.');
        }
        return true;
      } catch (error) {
        if (error instanceof ContentRevisionConflictError) {
          showWarningToast('Revision conflict — choose reload or save as copy.');
          const choice = await showRevisionConflictDialog();
          if (choice === 'reload') {
            await reloadContent();
          } else if (choice === 'copy') {
            const copyPath = copyPathForConflict(pathRef.current);
            await persist({ targetPath: copyPath, skipRevisionCheck: true });
          }
          return false;
        }

        const message = error instanceof Error ? error.message : 'Save failed.';
        if (error instanceof ApiHttpError && isFileAccessRestrictedStatus(error.status)) {
          navigate(fileAccessRestrictedHref(), { replace: true });
          return false;
        }
        showErrorToast(message);
        return false;
      } finally {
        savingRef.current = false;
        setSaving(false);
      }
    },
    [editor, navigate, reloadContent, setParams]
  );

  const onSave = useCallback(() => {
    void persist();
  }, [persist]);

  const insertImageFromFile = useCallback(
    async (file: File) => {
      if (!editor || !mountIdRef.current || !pathRef.current) return;
      setUploadingImage(true);
      try {
        const uploaded = await uploadJdocAsset(mountIdRef.current, pathRef.current, file);
        editor.chain().focus().setImage({ src: uploaded.previewUrl, alt: file.name }).run();
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Image upload failed.';
        showErrorToast(message);
      } finally {
        setUploadingImage(false);
      }
    },
    [editor]
  );

  const onImageButtonClick = useCallback(() => {
    imageInputRef.current?.click();
  }, []);

  const onImageInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (file) void insertImageFromFile(file);
    },
    [insertImageFromFile]
  );

  useEffect(() => {
    if (!editor) return;

    const onPaste = (event: ClipboardEvent) => {
      const items = event.clipboardData?.items;
      if (!items) return;
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) {
            event.preventDefault();
            void insertImageFromFile(file);
            return;
          }
        }
      }
    };

    const root = editor.view.dom;
    root.addEventListener('paste', onPaste);
    return () => root.removeEventListener('paste', onPaste);
  }, [editor, insertImageFromFile]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
        event.preventDefault();
        void persist();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [persist]);

  useEffect(() => {
    if (!dirty || loadState !== 'loaded') return;
    if (sourceBackedActive ? !sourceHydrated : !editorHydrated) return;
    const autosaveMs = getEditorAutosaveIntervalMs();
    if (!autosaveMs) return;
    const timer = window.setInterval(() => {
      void persist({ silent: true });
    }, autosaveMs);
    return () => window.clearInterval(timer);
  }, [dirty, editorHydrated, loadState, persist, sourceBackedActive, sourceHydrated]);

  const toggleWidthMode = useCallback(() => {
    setWidthMode((current) => {
      const next: WidthMode = current === 'full' ? 'constrained' : 'full';
      writeWidthPreference(next);
      return next;
    });
  }, []);

  const flushSourceIntoEditor = useCallback(() => {
    if (!editor) return;
    applyingSourceToEditorRef.current = true;
    try {
      editor.commands.setContent(sourceMarkdownRef.current);
    } finally {
      window.setTimeout(() => {
        applyingSourceToEditorRef.current = false;
      }, 0);
    }
  }, [editor]);

  const changeLayoutMode = useCallback(
    (next: LayoutMode) => {
      setLayoutMode((current) => {
        if (current === next) return current;
        if (current !== 'edit' && next === 'edit') {
          flushSourceIntoEditor();
        } else if (current === 'edit' && next !== 'edit') {
          const markdown = readLiveMarkdown(editor);
          setSourceMarkdown(markdown);
          sourceMarkdownRef.current = markdown;
          setPreviewMarkdown(markdown);
          setSourceHydrated(true);
          sourceHydratedRef.current = true;
        }
        writeMarkdownEditorLayoutPreference(next);
        return next;
      });
    },
    [editor, flushSourceIntoEditor]
  );

  // Live rendered output follows the source buffer in split and preview-only modes.
  useEffect(() => {
    if (!sourceBackedActive || !sourceHydrated) return;
    const timer = window.setTimeout(() => {
      setPreviewMarkdown(sourceMarkdown);
    }, PREVIEW_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [sourceBackedActive, sourceHydrated, sourceMarkdown]);

  // Keep TipTap/doc model in sync so Save/dirty still work after returning to Edit.
  useEffect(() => {
    if (!sourceBackedActive || !sourceHydrated || !editor || loadState !== 'loaded') return;
    const timer = window.setTimeout(() => {
      if (applyingSourceToEditorRef.current) return;
      const current = readLiveMarkdown(editor);
      if (current === sourceMarkdown) return;
      applyingSourceToEditorRef.current = true;
      try {
        editor.commands.setContent(sourceMarkdown);
      } finally {
        window.setTimeout(() => {
          applyingSourceToEditorRef.current = false;
        }, 0);
      }
    }, PREVIEW_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [sourceBackedActive, sourceHydrated, sourceMarkdown, editor, loadState]);

  const setLink = useCallback(() => {
    if (!editor) return;
    const previous = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('Link URL', previous ?? 'https://');
    if (url === null) return;
    if (!url.trim()) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url.trim() }).run();
  }, [editor]);

  if (!hasSource) {
    return (
      <div className="ops-fill-page ops-fill-page--bare markdown-editor-page">
        <div className="markdown-editor-page__empty">
          <p>
            Open a Markdown or JDoc file from the{' '}
            <Link to={filesLinkTo}>{filesLinkLabel}</Link> to edit rich text content.
          </p>
        </div>
      </div>
    );
  }

  const activePaneHydrated = sourceBackedActive ? sourceHydrated : editorHydrated;
  const toolbarDisabled =
    viewOnly || loadState !== 'loaded' || !activePaneHydrated || saving || uploadingImage;
  const richToolbarDisabled = toolbarDisabled || sourceBackedActive;

  const header = (
    <header
      className={`markdown-editor-page__header${embedded ? ' markdown-editor-page__header--embedded' : ''}`}
      aria-label="Markdown editor toolbar"
    >
      {chrome === 'full' && !embedded ? (
        <Link to={filesLinkTo} className="dx-link">
          {filesLinkLabel}
        </Link>
      ) : null}
      {!embedded ? (
        <span className="markdown-editor-page__title" title={title}>
          {title}
        </span>
      ) : null}
      <div className="markdown-editor-page__toolbar" role="toolbar" aria-label="Formatting">
        {!viewOnly ? (
          <>
            <Button
              icon="save"
              text="Save"
              stylingMode="contained"
              type="default"
              disabled={toolbarDisabled || !dirty}
              onClick={onSave}
            />
            <span className="markdown-editor-page__toolbar-sep" aria-hidden />
          </>
        ) : null}
        <Button
          icon="bold"
          stylingMode="text"
          hint={sourceBackedActive ? 'Available in Edit mode' : 'Bold'}
          disabled={richToolbarDisabled}
          onClick={() => editor?.chain().focus().toggleBold().run()}
        />
        <Button
          icon="italic"
          stylingMode="text"
          hint={sourceBackedActive ? 'Available in Edit mode' : 'Italic'}
          disabled={richToolbarDisabled}
          onClick={() => editor?.chain().focus().toggleItalic().run()}
        />
        <Button
          text="H1"
          stylingMode="text"
          hint={sourceBackedActive ? 'Available in Edit mode' : 'Heading 1'}
          disabled={richToolbarDisabled}
          onClick={() => editor?.chain().focus().toggleHeading({ level: 1 }).run()}
        />
        <Button
          text="H2"
          stylingMode="text"
          hint={sourceBackedActive ? 'Available in Edit mode' : 'Heading 2'}
          disabled={richToolbarDisabled}
          onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
        />
        <Button
          text="H3"
          stylingMode="text"
          hint={sourceBackedActive ? 'Available in Edit mode' : 'Heading 3'}
          disabled={richToolbarDisabled}
          onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()}
        />
        <Button
          icon="bulletlist"
          stylingMode="text"
          hint={sourceBackedActive ? 'Available in Edit mode' : 'Bullet list'}
          disabled={richToolbarDisabled}
          onClick={() => editor?.chain().focus().toggleBulletList().run()}
        />
        <Button
          icon="orderedlist"
          stylingMode="text"
          hint={sourceBackedActive ? 'Available in Edit mode' : 'Numbered list'}
          disabled={richToolbarDisabled}
          onClick={() => editor?.chain().focus().toggleOrderedList().run()}
        />
        <Button
          icon="check"
          stylingMode="text"
          hint={sourceBackedActive ? 'Available in Edit mode' : 'Task list'}
          disabled={richToolbarDisabled}
          onClick={() => editor?.chain().focus().toggleTaskList().run()}
        />
        <Button
          icon="link"
          stylingMode="text"
          hint={sourceBackedActive ? 'Available in Edit mode' : 'Link'}
          disabled={richToolbarDisabled}
          onClick={setLink}
        />
        <Button
          icon="codeblock"
          stylingMode="text"
          hint={sourceBackedActive ? 'Available in Edit mode' : 'Code block'}
          disabled={richToolbarDisabled}
          onClick={() => editor?.chain().focus().toggleCodeBlock().run()}
        />
        <Button
          icon="image"
          stylingMode="text"
          hint={sourceBackedActive ? 'Available in Edit mode' : 'Insert image'}
          disabled={richToolbarDisabled}
          onClick={onImageButtonClick}
        />
        <span className="markdown-editor-page__toolbar-sep" aria-hidden />
        <div className="markdown-editor-page__view-modes" role="group" aria-label="View mode">
          <Button
            icon="edit"
            text="Edit"
            stylingMode={layoutMode === 'edit' ? 'contained' : 'text'}
            disabled={viewOnly || loadState !== 'loaded'}
            hint="Rich-text editing"
            onClick={() => changeLayoutMode('edit')}
          />
          <Button
            icon="columnfield"
            text="Source + preview"
            stylingMode={layoutMode === 'split' ? 'contained' : 'text'}
            disabled={viewOnly || loadState !== 'loaded'}
            hint="Raw Markdown on the left, rendered preview on the right"
            onClick={() => changeLayoutMode('split')}
          />
          <Button
            icon="doc"
            text="Preview"
            stylingMode={layoutMode === 'preview' ? 'contained' : 'text'}
            disabled={loadState !== 'loaded'}
            hint="Read the final rendered document"
            onClick={() => changeLayoutMode('preview')}
          />
        </div>
        <Button
          icon="expand"
          text={effectiveWidthMode === 'full' ? 'Width: full' : 'Width: narrow'}
          stylingMode="text"
          disabled={loadState !== 'loaded'}
          onClick={toggleWidthMode}
        />
      </div>
      <div className="markdown-editor-page__meta" aria-live="polite">
        {docFormat ? (
          <span className="markdown-editor-page__badge">{formatLabel(docFormat)}</span>
        ) : null}
        {viewOnly ? <span className="markdown-editor-page__badge">View only</span> : null}
        {dirty ? <span className="markdown-editor-page__dirty">Unsaved changes</span> : null}
        {saving || uploadingImage ? <LoadIndicator height={18} width={18} visible /> : null}
        {loadState === 'error' && loadError ? (
          <span className="markdown-editor-page__badge markdown-editor-page__badge--error" role="alert">
            {loadError}
          </span>
        ) : null}
      </div>
    </header>
  );

  const stage = (
    <>
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        className="markdown-editor-page__hidden-input"
        onChange={onImageInputChange}
      />

      <div className="markdown-editor-page__stage">
        {loadState === 'loading' ? (
          <div className="markdown-editor-page__empty" aria-busy="true">
            <LoadIndicator height={36} width={36} visible />
          </div>
        ) : loadState === 'error' ? (
          <div className="markdown-editor-page__empty" role="alert">
            <p>{loadError ?? 'Failed to load file.'}</p>
            <Button text="Retry" stylingMode="contained" onClick={() => void reloadContent()} />
          </div>
        ) : (
          <div
            className={`markdown-editor-page__content${
              effectiveWidthMode === 'constrained'
                ? ' markdown-editor-page__content--constrained'
                : ' markdown-editor-page__content--full'
            }${splitActive ? ' markdown-editor-page__content--split' : ''}${
              previewOnlyActive ? ' markdown-editor-page__content--preview' : ''
            }`}
          >
            {splitActive ? (
              <>
                <div className="markdown-editor-page__pane markdown-editor-page__pane--source">
                  <MarkdownSourcePane
                    value={sourceMarkdown}
                    onChange={setSourceMarkdown}
                    readOnly={viewOnly || loadState !== 'loaded' || !sourceHydrated || saving}
                  />
                </div>
                <div className="markdown-editor-page__pane markdown-editor-page__pane--preview">
                  <MarkdownPreviewPane markdown={previewMarkdown} />
                </div>
              </>
            ) : previewOnlyActive ? (
              <div className="markdown-editor-page__pane markdown-editor-page__pane--preview-only">
                <MarkdownPreviewPane markdown={previewMarkdown} />
              </div>
            ) : (
              <div className="markdown-editor-page__pane markdown-editor-page__pane--editor">
                <EditorContent editor={editor} className="markdown-editor-page__editor" />
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );

  // Always show the formatting toolbar — including workspace iframe embeds.
  // Only the host AppShell / Files link are suppressed for embedded/minimal chrome.
  const pageBody = (
    <>
      {header}
      {stage}
    </>
  );

  return (
    <div
      className={`ops-fill-page ops-fill-page--bare markdown-editor-page${
        embedded ? ' markdown-editor-page--embedded' : ''
      }`}
    >
      {chrome === 'minimal' && !embedded ? (
        <MinimalEditorChrome title={title || 'Markdown Editor'}>{pageBody}</MinimalEditorChrome>
      ) : (
        pageBody
      )}
    </div>
  );
}
